import { useState, useRef, useEffect, useCallback } from 'react';
import { useNotifications } from '@medprompt/shared';

const displayedDocumentTitle = (doc) => doc?.overrideTitle || doc?.documentTitle || "Untitled";

// Split title into basename and extension (extension includes the dot)
const splitTitleAndExtension = (title) => {
  const lastDotIndex = title.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return { basename: title, extension: '' };
  }
  return {
    basename: title.substring(0, lastDotIndex),
    extension: title.substring(lastDotIndex)
  };
};

const useDocumentTitle = ({ doc, isEditingEnabled, documentService, updateDoc, loadKey }) => {
  const { addNotification } = useNotifications();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const titleInputRef = useRef(null);

  const docId = doc?.documentId;
  const currentTitle = displayedDocumentTitle(doc);
  const overrideTitle = doc?.overrideTitle;

  useEffect(() => {
    setIsEditingTitle(false);
  }, [loadKey]);

  // Focus the input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleTitleClick = useCallback(() => {
    if (!isEditingEnabled()) return;
    const { basename } = splitTitleAndExtension(currentTitle);
    setEditedTitle(basename);
    setIsEditingTitle(true);
  }, [isEditingEnabled, currentTitle]);

  const handleTitleChange = useCallback((e) => {
    setEditedTitle(e.target.value);
  }, []);

  const handleTitleBlur = useCallback(async () => {
    // If the field is empty/whitespace, clear the override
    if (editedTitle.trim() === '') {
      if (overrideTitle) {
        // Only send request if there's an override to clear
        try {
          await documentService.updateDocument(docId, { overrideTitle: "" });
          updateDoc(prevDoc => (prevDoc?.documentId === docId
            ? { ...prevDoc, overrideTitle: "" }
            : prevDoc));
          addNotification(docId, 'Title override cleared');
        } catch (error) {
          console.error('Error clearing title:', error);
          addNotification(docId, 'Failed to clear title', { isError: true });
        }
      }
      setIsEditingTitle(false);
      return;
    }

    const { extension } = splitTitleAndExtension(currentTitle);
    const newFullTitle = editedTitle + extension;

    if (newFullTitle !== currentTitle) {
      try {
        await documentService.updateDocument(docId, { overrideTitle: newFullTitle });
        updateDoc(prevDoc => (prevDoc?.documentId === docId
          ? { ...prevDoc, overrideTitle: newFullTitle }
          : prevDoc));
        addNotification(docId, 'Title updated successfully');
      } catch (error) {
        console.error('Error updating title:', error);
        addNotification(docId, 'Failed to update title', { isError: true });
      }
    }
    setIsEditingTitle(false);
  }, [editedTitle, overrideTitle, currentTitle, docId, documentService, updateDoc, addNotification]);

  const handleTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      titleInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  }, []);

  return {
    title: currentTitle,
    titleExtension: splitTitleAndExtension(currentTitle).extension,
    isEditingTitle,
    editedTitle,
    titleInputRef,
    handleTitleClick,
    handleTitleChange,
    handleTitleBlur,
    handleTitleKeyDown,
  };
};

export default useDocumentTitle;
